/**
 * Aba "E-mail Atendimento" da ficha da cliente, idêntica à do legado (`admin.html:2805-2808`,
 * `9603-9758`; linhas 226 a 229 do inventário).
 *
 * Lista os modelos de e-mail vinculados aos produtos do lead (`Produto · arquivo`), cada um com
 * "Baixar" (o modelo com o nome da cliente e a assinatura da Lu) e "Enviar E-mail" (a Edge Function
 * `enviar-email-atendimento` envia; a tela manda só identificadores).
 *
 * O SEGREDO DO E-MAIL NÃO PASSA POR AQUI: quem envia é a função, que lê destinatário e arquivo do
 * banco (`admin.html:9722-9725`).
 *
 * Território da frente A3. A moldura importa esta aba pelo nome `AbaEmailAtendimento`.
 */

// Estado e memória de função do React.
import { useCallback, useState } from 'react';
// O botão do Chassi.
import { Botao } from '@complexity/ui';
// A frase de cada erro de envio, portada do legado.
import { mensagemErroEnvioEmail } from '@studiolu/dados';
// Os modelos, o envio e o download.
import { enviarEmailAtendimento, lerModelosDeEmail } from '@studiolu/dados/areas/ficha-email-atendimento';
import { baixarArquivo } from '@studiolu/dados/areas/ficha-arquivos';
// O tipo da linha de modelo.
import type { LinhaParametrosProdutoEmail } from '@studiolu/dados';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// A confirmação e o aviso do legado, e o status que some sozinho.
import { useConfirmacao } from '../../../comum/confirmacao.js';
import { useStatusTemporario } from '../../../comum/status-temporario.js';
// O contrato da aba e as chaves de parâmetro dos produtos.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
import { chavesDeParametro } from '../comum-ficha/regras.js';
// As peças da A3.
import { PARAM_PRODUTOS_LABEL } from './comum-a3/rotulos.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
// A troca de texto do "Baixar".
import { preencherModelo } from './email-atendimento/modelo.js';

// O "Enviado!" some em 5 s, e não em 3 (`admin.html:9748-9750`).
const PRAZO_ENVIADO_MS = 5000;

/** O que a lista mostra. */
type Estado =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'sem-produto' }
  | { readonly tipo: 'erro'; readonly mensagem: string }
  | { readonly tipo: 'sem-modelo' }
  | { readonly tipo: 'modelos'; readonly linhas: readonly LinhaParametrosProdutoEmail[] };

export function AbaEmailAtendimento({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="E-mail Atendimento" />;
  return <ConteudoEmail ficha={ficha} />;
}

function ConteudoEmail({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  // As chaves de parâmetro do lead, como `produtosParamClienteAtual` (`admin.html:6019`), em texto
  // para o efeito comparar por valor.
  const chaves = chavesDeParametro(ficha.lead?.produtos ?? []).join('|');
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });

  // `carregarEmailAtendimento` (`admin.html:9641-9660`), pelo contrato de carga: a moldura roda
  // isto antes de mostrar a aba, e de novo a cada toque no botão dela. A lista é refeita a cada
  // carga, e é por isso que os status de cada linha somem: quem os apaga é o próprio carregador.
  const carregar = useCallback(async () => {
    setEstado({ tipo: 'carregando' });
    const produtosParam = chaves ? chaves.split('|') : [];
    // Sem produto, nenhuma consulta (`9645-9648`).
    if (produtosParam.length === 0) {
      setEstado({ tipo: 'sem-produto' });
      return;
    }
    const resultado = await lerModelosDeEmail(dados, produtosParam);
    // O erro mostra a mensagem crua depois de "Erro: " (`9655`).
    if (!resultado.ok) setEstado({ tipo: 'erro', mensagem: resultado.erro.mensagem });
    else if (!resultado.dados || resultado.dados.length === 0) setEstado({ tipo: 'sem-modelo' });
    else setEstado({ tipo: 'modelos', linhas: resultado.dados });
  }, [dados, chaves]);
  useCarregadorDaAba(carregar);

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">E-mail de Atendimento</h3>
      <div data-testid="email-ref-lista" className="flex flex-col gap-cx-1">
        {estado.tipo === 'carregando' && <p className="text-cx-sm text-texto-suave">Carregando…</p>}
        {estado.tipo === 'sem-produto' && <p className="text-cx-sm text-texto-suave">Nenhum produto vinculado a esta cliente.</p>}
        {estado.tipo === 'erro' && <p className="text-cx-sm text-texto-suave">{`Erro: ${estado.mensagem}`}</p>}
        {estado.tipo === 'sem-modelo' && (
          <p className="text-cx-sm text-texto-suave">Nenhum e-mail de referência vinculado aos produtos desta cliente.</p>
        )}
        {estado.tipo === 'modelos' &&
          estado.linhas.map((linha) => <LinhaDoModelo key={linha.id} linha={linha} ficha={ficha} />)}
      </div>
    </section>
  );
}

/** A cor do status de envio: neutra, de erro ou de sucesso (`admin.html:9704-9708`). */
type CorDoStatus = 'neutra' | 'erro' | 'sucesso';

// As classes de cada cor, todas de token.
const CLASSE_DA_COR: Readonly<Record<CorDoStatus, string>> = {
  neutra: 'text-texto-suave',
  erro: 'text-erro',
  sucesso: 'text-sucesso',
};

/** Uma linha de modelo, com o próprio status, como cada `statusEnvio` do legado. */
function LinhaDoModelo({ linha, ficha }: { readonly linha: LinhaParametrosProdutoEmail; readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const { confirmar, alertar, dialogo } = useConfirmacao();
  const [baixando, setBaixando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [status, mostrar, fixar] = useStatusTemporario(PRAZO_ENVIADO_MS);
  const [cor, setCor] = useState<CorDoStatus>('neutra');
  // A dica do status: o motivo técnico do erro, ou o destinatário do envio (`9737` e `9746`).
  const [dica, setDica] = useState('');

  // "Baixar" (`admin.html:9674-9691`): trava, baixa, preenche e salva como texto.
  const baixar = async () => {
    setBaixando(true);
    const resultado = await baixarArquivo(dados, linha.arquivo_path ?? '');
    if (!resultado.ok || !resultado.dados) {
      // O erro vai num aviso, com a mensagem crua (`9677`).
      await alertar('Erro ao baixar: ' + (resultado.ok ? '' : resultado.erro.mensagem));
      setBaixando(false);
      return;
    }
    const texto = preencherModelo(await resultado.dados.text(), ficha.cliente.nome_completo);
    // O arquivo sai como texto em UTF-8 com o nome original do modelo (`9681-9689`).
    const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain; charset=utf-8' }));
    const ancora = document.createElement('a');
    ancora.href = url;
    ancora.download = linha.arquivo_nome ?? '';
    document.body.appendChild(ancora);
    ancora.click();
    document.body.removeChild(ancora);
    URL.revokeObjectURL(url);
    setBaixando(false);
  };

  // "Enviar E-mail" (`admin.html:9710-9751`).
  const enviar = async () => {
    // Sem cadastro no portal não há para quem enviar (`9711-9714`).
    if (!ficha.cliente.user_id) {
      setCor('erro');
      fixar('Cliente sem cadastro no portal.');
      return;
    }
    // O e-mail mostrado é só o da ficha; quem escolhe o destinatário é a função, no banco.
    const destino = ficha.cliente.email || 'o e-mail cadastrado da cliente';
    if (!(await confirmar(`Enviar e-mail para ${destino}?`))) return;
    setEnviando(true);
    setDica('');
    setCor('neutra');
    fixar('Enviando…');
    const resultado = await enviarEmailAtendimento(dados, {
      user_id: ficha.cliente.user_id,
      lead_id: ficha.lead ? ficha.lead.id : null,
      produto: linha.produto,
    });
    setEnviando(false);
    if (!resultado.ok) {
      // A frase exata do legado, pelo código do corpo ou pelo status (`9626-9639`).
      const { curta, detalhe } = mensagemErroEnvioEmail({
        mensagem: resultado.erro.mensagem,
        status: resultado.erro.status,
        corpo: resultado.erro.corpo,
      });
      console.error('Falha ao enviar material por e-mail:', detalhe);
      setDica(detalhe || '');
      setCor('erro');
      // O erro FICA na tela até o próximo envio (`9738-9740`).
      fixar(curta);
      return;
    }
    setDica(resultado.dados?.destinatario ? `Enviado para ${resultado.dados.destinatario}` : '');
    setCor('sucesso');
    // "Enviado!" some em 5 s se ainda for o mesmo texto.
    mostrar('Enviado!');
  };

  return (
    <div data-testid="email-modelo" className="flex flex-wrap items-center justify-between gap-cx-2 border-b border-borda py-cx-1">
      <span className="min-w-0 break-all text-cx-sm text-texto">
        <strong>{PARAM_PRODUTOS_LABEL[linha.produto] || linha.produto}</strong>
        {` · ${linha.arquivo_nome ?? ''}`}
      </span>
      {/* Consulta não vê botão de ação (`admin.html:3395`). */}
      {!somenteLeitura && (
        <span className="flex shrink-0 items-center gap-cx-2">
          <Botao variante="secundario" tamanho="sm" disabled={baixando} onClick={() => void baixar()}>
            Baixar
          </Botao>
          <Botao tamanho="sm" disabled={enviando} onClick={() => void enviar()}>
            Enviar E-mail
          </Botao>
        </span>
      )}
      {/* O status ocupa a linha inteira abaixo dos botões, para a frase longa não estourar no
          celular (`admin.html:9698-9703`). */}
      {status && (
        <span data-testid="status-envio" title={dica} className={`basis-full break-words text-cx-xs ${CLASSE_DA_COR[cor]}`}>
          {status}
        </span>
      )}
      {dialogo}
    </div>
  );
}
